import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { mediaManagerClient } from '@/lib/axios';
import { MutationConfig } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';
import { State } from '@/types';
import { emptyStringValues } from '@/utils/objects';

import { TLogo } from '../types';

import { logosQueryKeys } from './query-keys';
import { useUpdateLogoImage } from './update-logos-images';

export type TPatchLogosParams = {
  id: string;
  description: string | null;
  parameters: {
    main_logo?: File | string;
    logos?: File[] | string[];
    include_in_clip_summary?: boolean;
  } | null;
  main_logo?: File | string;
  logos: File[] | string[];
  state: State;
};

export async function patchLogos({ ...data }: TPatchLogosParams): Promise<TLogo> {
  return mediaManagerClient.patch(`/api/v1/logos/${data.id}`, emptyStringValues(data));
}

type MutationFn = typeof patchLogos;

type TUsePatchLogosParams = {
  config?: MutationConfig<MutationFn>;
};

export function usePatchLogos({ config }: TUsePatchLogosParams = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate: updateLogoImages } = useUpdateLogoImage();

  const { addErrorNotification, addSuccessNotification } = useNotificationStore();

  return useMutation({
    onError(error) {
      addErrorNotification(t('No se ha podido actualizar el logo'));
      console.error(error);
    },
    onSuccess(logo, variables) {
      addSuccessNotification(t('El logo se ha actualizado correctamente'));
      queryClient.invalidateQueries({ queryKey: logosQueryKeys.all });
      if (variables.logos || variables.main_logo) {
        updateLogoImages({
          id: logo.id,
          main_logo: variables.main_logo,
          logos: variables.logos,
          state: variables.state,
        });
      }
    },
    ...config,
    mutationFn: patchLogos,
  });
}
